import React from "react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { LogOut, Settings, CreditCard, AlertTriangle } from "lucide-react";
import { NotificationBell } from "@/components/notification-bell";
import { DensityToggle } from "@/components/ui/density-toggle";

interface BillingData {
  subscription: {
    status: 'trial' | 'active' | 'past_due' | 'cancelled' | 'paused';
    trialEnd?: string | null;
  } | null;
  plan: {
    name: string;
  } | null;
  planExpiresAt?: string | null;
}

interface NotificationData {
  notifications: Array<{
    id: number;
    title: string;
    dueDate: string;
    type: string;
  }>;
  counts: {
    overdue: number;
    today: number;
    upcoming: number;
    badge: number;
  };
}

export function BottomSettingsBar() {
  const { logoutMutation } = useAuth();
  const { t } = useTranslation(['common']);

  const { data: billingData } = useQuery<BillingData>({
    queryKey: ['/api/billing'],
    staleTime: 1000 * 60 * 5, // 5 minút
  });

  // Načítanie urgentných úloh
  const { data: notifications } = useQuery<NotificationData>({
    queryKey: ["/api/notifications"],
    refetchInterval: 60000, // Refresh every minute
  });

  const urgentTasksCount = (notifications?.counts?.today || 0) + (notifications?.counts?.overdue || 0);

  // Vypočítaj zostávajúce dni trial
  const getTrialDaysLeft = () => {
    if (billingData?.subscription?.status === 'trial' && billingData?.subscription?.trialEnd) {
      const trialEnd = new Date(billingData.subscription.trialEnd);
      const now = new Date();
      const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysLeft > 0) {
        return daysLeft;
      }
    }
    return null;
  };

  const trialDaysLeft = getTrialDaysLeft();

  const getPlanBadge = () => {
    if (!billingData?.subscription || !billingData?.plan) {
      return <Badge variant="outline" className="text-xs">{t('plans.free', { ns: 'common' }) || t('common:plans.free') || 'Zadarmo'}</Badge>;
    }

    const { subscription, plan } = billingData;
    const statusColors = {
      active: 'default',
      trial: 'secondary',
      cancelled: 'destructive',
      past_due: 'destructive',
      paused: 'secondary'
    } as const;

    return (
      <Badge 
        variant={statusColors[subscription.status] || 'outline'} 
        className="text-xs"
      >
        {plan.name}
      </Badge>
    );
  };

  return (
    <div className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t border-border shrink-0 overflow-x-auto" data-onboarding="bottom-settings">
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2 px-2 py-2 sm:px-4 sm:py-3 w-full min-w-0 max-w-full">
        {/* Sekcia Nastavenia */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 order-1">
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Settings className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium text-muted-foreground hidden sm:inline whitespace-nowrap">
              {t('common:settings') || 'Nastavenia'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <NotificationBell />
            <DensityToggle />
            <ThemeToggle />
          </div>
        </div>

        {/* Aktuálny plán – na mobile skrátený */}
        <div className="flex items-center gap-1 sm:gap-2 min-w-0 order-3 sm:order-2 flex-1 sm:flex-initial justify-center shrink-0">
          <CreditCard className="h-4 w-4 text-muted-foreground hidden sm:block shrink-0" />
          <span className="text-sm text-muted-foreground hidden lg:inline whitespace-nowrap">{t('plan', { ns: 'common' }) || t('common:plan') || 'Plán'}:</span>
          <span className="min-w-0 max-w-[90px] sm:max-w-[140px] lg:max-w-none overflow-hidden truncate text-xs sm:text-sm">{getPlanBadge()}</span>
          {trialDaysLeft !== null && (
            <Badge variant="secondary" className="text-xs shrink-0 ml-1 sm:ml-2 bg-blue-600 text-white font-semibold whitespace-nowrap">
              {trialDaysLeft} {trialDaysLeft === 1 ? 'deň' : trialDaysLeft < 5 ? 'dni' : 'dní'}
            </Badge>
          )}
        </div>

        {/* Tlačidlo Odhlásiť sa */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0 order-2 sm:order-3">
          <Button
            variant="ghost"
            size="sm"
            className="min-h-[44px] min-w-[44px] h-11 sm:h-9 sm:min-h-0 sm:min-w-0 text-red-600 hover:text-red-700 hover:bg-red-100 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 px-3 sm:px-3 touch-manipulation"
            onClick={() => logoutMutation.mutate()}
          >
            <LogOut className="h-4 w-4 sm:mr-1.5 shrink-0" />
            <span className="hidden md:inline whitespace-nowrap">Odhlásiť sa</span>
          </Button>
        </div>
      </div>
    </div>
  );
}